import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-eth/admin/purchases`;

export const AdminEthPurchases = ({ token }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(API, { headers: { Authorization: `Bearer ${token}` } }).then(r => setD(r.data)).catch(e => setErr(e.response?.data?.detail || 'Could not load ETH sales')), [token]);
  useEffect(() => { load(); }, [load]);
  const retry = async (id) => {
    try { const r = await axios.post(`${API}/${id}/retry`, {}, h); toast[r.data.status === 'completed' ? 'success' : 'error'](r.data.error || 'ETH sent'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };
  if (err) return <p className="text-red-400" data-testid="admin-eth-error">{err}</p>;
  if (!d) return <p className="text-slate-400">Loading...</p>;
  return (
    <div className="space-y-4" data-testid="admin-eth-purchases">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 text-sm text-slate-300">
        <p>Treasury Reserve Wallet <span className="font-mono text-xs text-emerald-400">{d.master_wallet}</span> on {d.network}</p>
        <p className="mt-1" data-testid="admin-eth-balance">Balance {d.master_eth_balance ?? '—'} ETH · available to sell {d.available_eth ?? '—'} ETH · fee {d.fee_pct}%</p>
        <p className="text-xs text-slate-500 mt-1">Send ETH to this address to enable customer purchases. Paid card orders awaiting funding can be retried below.</p>
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900"><tr>{['Order', 'Customer', 'ETH', 'Paid', 'Status', ''].map(x => <th key={x} className="text-left text-slate-400 font-medium px-4 py-3">{x}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-700">
            {d.purchases.map(p => (
              <tr key={p.purchase_id} data-testid={`admin-eth-row-${p.purchase_id}`}>
                <td className="px-4 py-3 font-mono text-xs text-white">{p.purchase_id}<p className="text-slate-500">{new Date(p.created_at).toLocaleString()}</p></td>
                <td className="px-4 py-3 text-xs text-slate-300">{p.email}<p className="font-mono text-slate-500 break-all">{p.to_address}</p></td>
                <td className="px-4 py-3 text-white">{p.eth_amount}<p className="text-xs text-slate-500">@ ${p.eth_price_usd}</p></td>
                <td className="px-4 py-3 text-slate-300">${Number(p.usd_amount).toFixed(2)}<p className="text-xs text-slate-500">{p.payment_method === 'beth' ? `${p.beth_cost} BETH` : 'Card'} · fee ${p.fee_usd}</p></td>
                <td className="px-4 py-3 text-xs"><span className={p.status === 'completed' ? 'text-emerald-400' : 'text-amber-300'}>{p.status.replace(/_/g, ' ')}</span>{p.error && <p className="text-red-400 max-w-[220px]">{p.error}</p>}</td>
                <td className="px-4 py-3">
                  {p.explorer_url && <a href={p.explorer_url} target="_blank" rel="noreferrer" className="text-purple-400"><ExternalLink className="w-4 h-4" /></a>}
                  {['paid_pending_funding', 'needs_review'].includes(p.status) && <button onClick={() => retry(p.purchase_id)} data-testid={`admin-eth-retry-${p.purchase_id}`} className="text-sky-400 flex items-center gap-1 text-xs"><RefreshCw className="w-3.5 h-3.5" /> Retry send</button>}
                </td>
              </tr>
            ))}
            {!d.purchases.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No ETH purchases yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
